export const PHOTO_UPLOAD_DB_NAME = "autoeinfachlos-photo-uploads";
export const PHOTO_UPLOAD_DB_VERSION = 2;
export const PHOTO_UPLOAD_STORE_NAME = "photo-upload-records";
const PHOTO_UPLOAD_OWNER_INDEX = "ownerEmail";
const DEFAULT_DATABASE_OPEN_TIMEOUT_MS = 8_000;
const DEFAULT_TRANSACTION_TIMEOUT_MS = 15_000;
const TERMINAL_STATES = new Set([
  "deleting",
  "confirmed",
  "deleted",
  "superseded",
]);
const UPLOAD_STATE_RANK = {
  pending: 0,
  failed: 1,
  committed: 2,
  superseded: 3,
  deleted: 4,
};

let databasePromise = null;
let openDatabase = null;
let databaseOpenTimeoutMs = DEFAULT_DATABASE_OPEN_TIMEOUT_MS;
let transactionTimeoutMs = DEFAULT_TRANSACTION_TIMEOUT_MS;

function createStorageTimeoutError(operation) {
  const error = new Error(
    `IndexedDB hat beim ${operation} nicht rechtzeitig geantwortet.`
  );
  error.name = "PhotoUploadStorageTimeoutError";
  error.code = "PHOTO_UPLOAD_STORAGE_TIMEOUT";
  return error;
}

function assertKey(key) {
  if (typeof key !== "string" || !key.trim()) {
    throw new TypeError("Ein Foto-Upload benötigt einen gültigen key.");
  }
}

function assertRecord(record) {
  if (!record || typeof record !== "object" || Array.isArray(record)) {
    throw new TypeError("Ein Foto-Upload-Record muss ein Objekt sein.");
  }
  assertKey(record.key);
}

function openPhotoUploadDatabase() {
  if (!globalThis?.indexedDB) {
    throw new Error("IndexedDB ist in dieser Umgebung nicht verfügbar.");
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const request = globalThis.indexedDB.open(
      PHOTO_UPLOAD_DB_NAME,
      PHOTO_UPLOAD_DB_VERSION
    );
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(createStorageTimeoutError("Öffnen"));
    }, databaseOpenTimeoutMs);

    const fail = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      reject(error);
    };

    request.onupgradeneeded = () => {
      const database = request.result;
      const store = database.objectStoreNames.contains(PHOTO_UPLOAD_STORE_NAME)
        ? request.transaction.objectStore(PHOTO_UPLOAD_STORE_NAME)
        : database.createObjectStore(PHOTO_UPLOAD_STORE_NAME, {
          keyPath: "key",
        });
      if (!store.indexNames.contains(PHOTO_UPLOAD_OWNER_INDEX)) {
        store.createIndex(PHOTO_UPLOAD_OWNER_INDEX, "ownerEmail", {
          unique: false,
        });
      }
    };
    request.onerror = () => {
      fail(request.error || new Error("IndexedDB konnte nicht geöffnet werden."));
    };
    request.onblocked = () => {
      fail(new Error("IndexedDB ist durch eine andere Seite blockiert."));
    };
    request.onsuccess = () => {
      const database = request.result;
      if (settled) {
        database.close();
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      openDatabase = database;
      database.onversionchange = () => {
        database.close();
        if (openDatabase === database) openDatabase = null;
        databasePromise = null;
      };
      resolve(database);
    };
  });
}

function getDatabase() {
  if (!databasePromise) {
    const nextPromise = openPhotoUploadDatabase();
    databasePromise = nextPromise;
    void nextPromise.catch(() => {
      if (databasePromise === nextPromise) databasePromise = null;
    });
  }
  return databasePromise;
}

async function runTransaction(mode, work) {
  const database = await getDatabase();

  return new Promise((resolve, reject) => {
    let result;
    let settled = false;
    const transaction = database.transaction(PHOTO_UPLOAD_STORE_NAME, mode);
    const store = transaction.objectStore(PHOTO_UPLOAD_STORE_NAME);
    const timeoutId = setTimeout(() => {
      fail(createStorageTimeoutError("Speichern"));
      database.close();
      if (openDatabase === database) openDatabase = null;
      databasePromise = null;
    }, transactionTimeoutMs);

    const fail = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      try {
        transaction.abort();
      } catch {}
      reject(error || new Error("IndexedDB-Transaktion fehlgeschlagen."));
    };

    transaction.oncomplete = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      resolve(result);
    };
    transaction.onerror = () => fail(transaction.error);
    transaction.onabort = () => fail(transaction.error);

    try {
      work(store, (value) => {
        result = value;
      }, fail);
    } catch (error) {
      fail(error);
    }
  });
}

function isIncomingRecordCurrent(existing, incoming) {
  if (!existing) return true;
  if (existing.uploadId === incoming.uploadId) {
    if (existing.state === "deleting" && incoming.state === "confirmed") {
      return false;
    }
    return (
      !TERMINAL_STATES.has(existing.state) ||
      TERMINAL_STATES.has(incoming.state)
    );
  }

  const existingCapturedAt = Number(existing.capturedAt || 0);
  const incomingCapturedAt = Number(incoming.capturedAt || 0);
  if (existingCapturedAt !== incomingCapturedAt) {
    return incomingCapturedAt > existingCapturedAt;
  }

  return String(incoming.uploadId || "") > String(existing.uploadId || "");
}

export async function putPhotoUploadRecordIfCurrent(record) {
  assertRecord(record);

  return runTransaction("readwrite", (store, setResult, fail) => {
    const getRequest = store.get(record.key);
    getRequest.onerror = () => fail(getRequest.error);
    getRequest.onsuccess = () => {
      if (!isIncomingRecordCurrent(getRequest.result, record)) {
        setResult(false);
        return;
      }
      const putRequest = store.put(record);
      putRequest.onerror = () => fail(putRequest.error);
      putRequest.onsuccess = () => setResult(true);
    };
  });
}

export async function updatePhotoUploadRecord(key, uploadId, patch) {
  assertKey(key);

  return runTransaction("readwrite", (store, setResult, fail) => {
    const getRequest = store.get(key);
    getRequest.onerror = () => fail(getRequest.error);
    getRequest.onsuccess = () => {
      const current = getRequest.result;
      if (
        !current ||
        current.uploadId !== uploadId ||
        TERMINAL_STATES.has(current.state)
      ) {
        setResult(false);
        return;
      }
      const currentRank = UPLOAD_STATE_RANK[current.state] ?? 0;
      const nextRank = UPLOAD_STATE_RANK[patch?.state] ?? currentRank;
      if (nextRank < currentRank) {
        setResult(false);
        return;
      }
      const putRequest = store.put({ ...current, ...patch, key, uploadId });
      putRequest.onerror = () => fail(putRequest.error);
      putRequest.onsuccess = () => setResult(true);
    };
  });
}

export async function getPhotoUploadRecord(key) {
  assertKey(key);

  return runTransaction("readonly", (store, setResult, fail) => {
    const request = store.get(key);
    request.onerror = () => fail(request.error);
    request.onsuccess = () => setResult(request.result || null);
  });
}

export async function getPhotoUploadRecordsByOwner(ownerEmail) {
  if (!ownerEmail) return [];

  return runTransaction("readonly", (store, setResult, fail) => {
    const request = store
      .index(PHOTO_UPLOAD_OWNER_INDEX)
      .getAll(String(ownerEmail).trim().toLowerCase());
    request.onerror = () => fail(request.error);
    request.onsuccess = () => setResult(request.result || []);
  });
}

export async function markPhotoUploadDeleting(key, uploadId) {
  assertKey(key);

  return runTransaction("readwrite", (store, setResult, fail) => {
    const getRequest = store.get(key);
    getRequest.onerror = () => fail(getRequest.error);
    getRequest.onsuccess = () => {
      const current = getRequest.result;
      if (!current || current.uploadId !== uploadId) {
        setResult({ ok: false, current: current || null });
        return;
      }
      if (
        ["confirmed", "deleted", "superseded"].includes(current.state)
      ) {
        setResult({ ok: false, current });
        return;
      }
      const next = {
        ...current,
        stateBeforeDelete:
          current.state === "deleting"
            ? current.stateBeforeDelete
            : current.state,
        phaseBeforeDelete:
          current.state === "deleting"
            ? current.phaseBeforeDelete
            : current.phase || null,
        state: "deleting",
        phase: null,
        updatedAt: Date.now(),
      };
      const putRequest = store.put(next);
      putRequest.onerror = () => fail(putRequest.error);
      putRequest.onsuccess = () => setResult({ ok: true, record: next });
    };
  });
}

export async function restorePhotoUploadAfterDeleteFailure(key, uploadId) {
  assertKey(key);

  return runTransaction("readwrite", (store, setResult, fail) => {
    const getRequest = store.get(key);
    getRequest.onerror = () => fail(getRequest.error);
    getRequest.onsuccess = () => {
      const current = getRequest.result;
      if (
        !current ||
        current.uploadId !== uploadId ||
        current.state !== "deleting"
      ) {
        setResult({ ok: false, current: current || null });
        return;
      }
      const next = { ...current };
      next.state =
        current.stateBeforeDelete ||
        (current.imageUrl ? "committed" : "pending");
      next.phase =
        next.state === "pending"
          ? current.phaseBeforeDelete || "queued"
          : null;
      delete next.stateBeforeDelete;
      delete next.phaseBeforeDelete;
      next.updatedAt = Date.now();
      const putRequest = store.put(next);
      putRequest.onerror = () => fail(putRequest.error);
      putRequest.onsuccess = () => setResult({ ok: true, record: next });
    };
  });
}

export async function tombstonePhotoUploadRecord(record) {
  assertRecord(record);

  return runTransaction("readwrite", (store, setResult, fail) => {
    const getRequest = store.get(record.key);
    getRequest.onerror = () => fail(getRequest.error);
    getRequest.onsuccess = () => {
      const current = getRequest.result;
      if (current && !isIncomingRecordCurrent(current, record)) {
        setResult(false);
        return;
      }
      const next = {
        ...(current || {}),
        ...record,
        blob: null,
        locationBlob: null,
        state: "deleted",
        updatedAt: Date.now(),
      };
      delete next.stateBeforeDelete;
      delete next.phaseBeforeDelete;
      const putRequest = store.put(next);
      putRequest.onerror = () => fail(putRequest.error);
      putRequest.onsuccess = () => setResult(true);
    };
  });
}

export async function clearPhotoUploadRecordsForTests() {
  return runTransaction("readwrite", (store, setResult, fail) => {
    const request = store.clear();
    request.onerror = () => fail(request.error);
    request.onsuccess = () => setResult(true);
  });
}

export function closePhotoUploadStoreForTests() {
  openDatabase?.close();
  openDatabase = null;
  databasePromise = null;
  databaseOpenTimeoutMs = DEFAULT_DATABASE_OPEN_TIMEOUT_MS;
  transactionTimeoutMs = DEFAULT_TRANSACTION_TIMEOUT_MS;
}

export function setPhotoUploadStoreTimeoutsForTests({
  openTimeoutMs = DEFAULT_DATABASE_OPEN_TIMEOUT_MS,
  transactionDeadlineMs = DEFAULT_TRANSACTION_TIMEOUT_MS,
} = {}) {
  databaseOpenTimeoutMs = Math.max(1, Number(openTimeoutMs) || 1);
  transactionTimeoutMs = Math.max(1, Number(transactionDeadlineMs) || 1);
}

export function __isIncomingPhotoUploadRecordCurrentForTests(
  existing,
  incoming
) {
  return isIncomingRecordCurrent(existing, incoming);
}
